/**
 * React Query Provider for 2W Admin Dashboard
 * Provides global query client configuration and error handling
 */

import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Create a client with optimized configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global defaults that can be overridden in individual hooks
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        
        // Retry up to 3 times for server errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus in production, but not in development
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',
      // Refetch on reconnect
      refetchOnReconnect: 'always',
      // Background refetch interval (10 minutes)
      refetchInterval: 10 * 60 * 1000,
      // Don't refetch in background if window is not visible
      refetchIntervalInBackground: false,
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry mutations on client errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        
        // Retry once for server errors
        return failureCount < 1;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Global error handler for queries
queryClient.setMutationDefaults(['analytics'], {
  mutationFn: async (variables) => {
    throw new Error('Analytics mutations not implemented');
  },
});

// Set up global query error handler
queryClient.setQueryDefaults(['analytics'], {
  onError: (error) => {
    console.error('Analytics query error:', error);
  },
});

export const QueryProvider = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Only show devtools in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools 
          initialIsOpen={false}
          position="bottom-right"
          toggleButtonProps={{
            style: {
              marginLeft: '5px',
              transform: 'scale(0.8)',
              transformOrigin: 'bottom right',
            }
          }}
        />
      )}
    </QueryClientProvider>
  );
};

// Custom hook to access query client
export const useQueryClientInstance = () => {
  return queryClient;
};

// Helper function to clear all caches (useful for logout)
export const clearAllCaches = () => {
  queryClient.clear();
};

// Helper function to prefetch common data
export const prefetchCommonData = async () => {
  try {
    // Prefetch brands data (used throughout the app)
    await queryClient.prefetchQuery({
      queryKey: ['brands'],
      queryFn: async () => {
        const { default: apiClient } = await import('../utils/api.js');
        return apiClient.getBrands();
      },
      staleTime: 10 * 60 * 1000, // 10 minutes
    });

    // Prefetch analytics overview
    await queryClient.prefetchQuery({
      queryKey: ['analytics', 'overview'],
      queryFn: async () => {
        const { default: apiClient } = await import('../utils/api.js');
        return apiClient.getAnalyticsOverview();
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  } catch (error) {
    console.warn('Failed to prefetch common data:', error);
  }
};

// Helper to invalidate data on authentication changes
export const handleAuthChange = (isAuthenticated) => {
  if (!isAuthenticated) {
    // Clear all data when user logs out
    clearAllCaches();
  } else {
    // Prefetch common data when user logs in
    prefetchCommonData();
  }
};

export default QueryProvider;